import React, { Component, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Flag } from 'flagger/react';
import { connect } from 'react-redux';
import UserPopover from './popovers/UserPopover';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import FusionBannerTrans from '../images/FusionBannerTrans.png';

// Page permissions grouped under another page's dropdown instead of taking a
// slot on the bar. Key = the host page permission. The host's bar item is a
// menu trigger (like Administration); its own page appears as the first entry
// of the panel under `selfLabel`, followed by the grouped pages.
const NAV_GROUPS = {
  configuration: {
    selfLabel: 'System Configuration',
    children: ['print_server_connections', 'print_destinations', 'polling_service'],
  },
};

// Width reserved for the "More" trigger while it is not rendered.
const MORE_RESERVE_PX = 96;
// Horizontal padding of the app bar (see .s-header-appbar in global.css).
const BAR_PADDING_PX = 24;

// Wrap a nav node in the EDM feature flag when the page permission asks for it.
function withFlag(entry, node) {
  return entry.edm ? (
    <Flag key={`flag-${entry.key}`} flag="EDM" entity={{ id: 1 }} case="on">
      {node}
    </Flag>
  ) : node;
}

function panelLink(entry) {
  return withFlag(entry, (
    <li key={entry.key} className={entry.active ? 's-active' : undefined}>
      <Link to={entry.base}><span>{entry.selfLabel || entry.label}</span></Link>
    </li>
  ));
}

function entryIsActive(entry) {
  return entry.active || (entry.children || []).some((c) => c.active);
}

/**
 * Top nav with overflow. Entries render centred on the bar; whatever does not
 * fit collapses, from the right, into a "More" dropdown. Administration stays
 * pinned beside More. The logo (left) and user menu (right) sit outside the
 * flow, so the fit calculation keeps clear of both. Widths are measured once
 * with everything visible, then the fit is recomputed from the cache on
 * resize, so nothing flickers.
 */
function TopNav({ entries, admin, userSession }) {
  const ulRef = useRef(null);
  const widths = useRef({});
  const needMeasure = useRef(true);
  const total = entries.length;
  const [count, setCount] = useState(total);
  const entriesKey = entries.map((e) => `${e.key}:${e.label}`).join('|');

  const recompute = useCallback(() => {
    const ul = ulRef.current;
    if (!ul) return;
    ul.querySelectorAll(':scope > li[data-nav-key]').forEach((li) => {
      widths.current[li.dataset.navKey] = li.offsetWidth;
    });
    const moreLi = ul.querySelector(':scope > li[data-nav-fixed="more"]');
    const moreWidth = moreLi ? moreLi.offsetWidth : MORE_RESERVE_PX;
    let fixed = 0;
    ul.querySelectorAll(':scope > li[data-nav-fixed]').forEach((li) => {
      const kind = li.dataset.navFixed;
      if (kind !== 'more' && kind !== 'profile') fixed += li.offsetWidth;
    });
    // Items are centred, so keep the same clearance on both sides: the wider
    // of the logo and the user menu, plus the bar padding.
    const header = ul.parentElement;
    const logo = header ? header.querySelector('.s-appbar-logo') : null;
    const profile = ul.querySelector(':scope > li[data-nav-fixed="profile"]');
    const side = Math.max(logo ? logo.offsetWidth : 0, profile ? profile.offsetWidth : 0) + BAR_PADDING_PX;
    const available = ul.clientWidth - fixed - 2 * side;
    let used = 0;
    let fits = 0;
    for (const e of entries) {
      const w = widths.current[e.key] || 0;
      const reserve = fits + 1 < total ? moreWidth : 0;
      if (used + w + reserve > available) break;
      used += w;
      fits += 1;
    }
    setCount(fits);
  }, [entries, total]);

  // New set of entries: show everything once so every width gets measured.
  useLayoutEffect(() => {
    needMeasure.current = true;
    setCount(total);
  }, [entriesKey, total]);

  useLayoutEffect(() => {
    if (needMeasure.current && count === total) {
      needMeasure.current = false;
      recompute();
    }
  }, [count, total, entriesKey, recompute]);

  useEffect(() => {
    const ul = ulRef.current;
    if (!ul) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recompute);
      return () => window.removeEventListener('resize', recompute);
    }
    // Recompute on the next frame: re-rendering synchronously inside the
    // observer callback changes layout in the same frame and trips the
    // browser's "ResizeObserver loop" warning.
    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(recompute);
    });
    ro.observe(ul);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [recompute]);

  const visible = entries.slice(0, count);
  const overflow = entries.slice(count);
  const moreActive = overflow.some(entryIsActive);

  const barItem = (e) => {
    const active = entryIsActive(e) ? ' s-active' : '';
    if (e.children && e.children.length > 0) {
      // Group host: a menu trigger whose panel opens with the host's own page
      // (under its selfLabel) followed by the pages grouped under it.
      return withFlag(e, (
        <li key={e.key} data-nav-key={e.key} className={`s-list-li s-sitemap-mega-module-li has-dropdown${active}`}>
          <button type="button" className="s-sitemap-mega-module-btn-default dropdown-trigger" aria-haspopup="menu">
            {e.label} <span className="caret" aria-hidden="true">▾</span>
          </button>
          <ul className="s-dropdown dropdown-panel">
            {panelLink(e)}
            {e.children.map(panelLink)}
          </ul>
        </li>
      ));
    }
    return withFlag(e, (
      <li key={e.key} data-nav-key={e.key} className={`s-list-li s-sitemap-mega-module-li${active}`}>
        <Link to={e.base} className="s-sitemap-mega-module-btn-default">{e.label}</Link>
      </li>
    ));
  };

  // In the More panel a group host is flattened: its own link, then its pages.
  const overflowItems = overflow.flatMap((e) => [e, ...(e.children || [])]);

  return (
    <ul ref={ulRef} className="s-sitemap-mega-modules">
      {visible.map(barItem)}

      {overflow.length > 0 && (
        <li data-nav-fixed="more" className={`s-list-li s-sitemap-mega-module-li has-dropdown s-nav-more${moreActive ? ' s-active' : ''}`}>
          <button type="button" className="s-sitemap-mega-module-btn-default dropdown-trigger" aria-haspopup="menu">
            More <span className="caret" aria-hidden="true">▾</span>
          </button>
          <ul className="s-dropdown dropdown-panel">
            {overflowItems.map(panelLink)}
          </ul>
        </li>
      )}

      {admin && withFlag(admin, (
        <li key={admin.key} data-nav-fixed="admin" className={`s-list-li s-sitemap-mega-module-li has-dropdown${admin.active ? ' s-active' : ''}`}>
          <button type="button" className="s-sitemap-mega-module-btn-default dropdown-trigger" aria-haspopup="menu">
            {admin.label} <span className="caret" aria-hidden="true">▾</span>
          </button>
          <ul className="s-dropdown dropdown-panel">
            <li><Link to="/site/administration/Users"><span>Users</span></Link></li>
            <li><Link to="/site/administration/ChangePassword"><span>Change Password</span></Link></li>
            <li><Link to="/site/administration/UserGroups"><span>User Groups</span></Link></li>
            <li><Link to="/site/administration/ImportUsers"><span>Import Users</span></Link></li>
          </ul>
        </li>
      ))}

      {userSession}
    </ul>
  );
}

class Header extends Component {
  constructor(props) {
    super(props);

    this.state = {
      userPopoverVisible: false,
    };
  }

  render() {
    let header = [];
    const { user, localisation, viewpermissions, deploymentType } = this.props.userReducer;
    const { isAuthenticated } = this.props.authReducer;
    const isCloud = deploymentType === 'cloud';

    let userPopover = <div />;
    let userSession = <div />;

    // Current route, for marking the active nav item. withRouter supplies
    // location so the header re-renders on navigation.
    const currentPath = (this.props.location?.pathname || window.location.pathname || '').toLowerCase();

    // Nav entries from page permissions: bar items (some hosting a group of
    // pages in a dropdown) and the pinned Administration dropdown.
    const byKey = {};
    const order = [];
    let admin = null;

    if (viewpermissions) {
      Object.keys(viewpermissions).forEach((k) => {
        const vp = viewpermissions[k];
        if (!vp?.enabled || !vp?.header) return;
        if (!vp.edm && !vp.edd) return;

        // Hide the Poller Service menu item on cloud deployments.
        // The item must be absent from the DOM, not merely CSS-hidden.
        if (isCloud && k === 'polling_service') return;

        const label = localisation?.[`menu_header_${k}`]?.element_lang ?? 'Editor';
        const rawLink = vp.link || '';
        const base = rawLink.replace(/\/$/, ''); // trim trailing slash
        const isAdmin = /\/administration(\/|$)/i.test(rawLink); // matches "/administration" and "/site/administration"
        const active = base !== '' && currentPath.startsWith(base.toLowerCase());
        const entry = { key: k, label, base, active, edm: !!vp.edm, children: [] };

        if (isAdmin) admin = entry;
        else {
          byKey[k] = entry;
          order.push(k);
        }
      });
    }

    // Attach grouped pages to their host. A host that is missing (no
    // permission) leaves its pages on the bar rather than hiding them.
    const grouped = new Set();
    Object.keys(NAV_GROUPS).forEach((hostKey) => {
      const host = byKey[hostKey];
      if (!host) return;
      const group = NAV_GROUPS[hostKey];
      host.selfLabel = group.selfLabel;
      group.children.forEach((childKey) => {
        if (byKey[childKey]) {
          host.children.push(byKey[childKey]);
          grouped.add(childKey);
        }
      });
    });
    const entries = order.filter((k) => !grouped.has(k)).map((k) => byKey[k]);

    if (this.props.visible) {
      if (isAuthenticated) {
        userPopover = <UserPopover visible={this.state.userPopoverVisible} />;
        userSession = (
          <li className="s-list-li s-sitemap-mega-module-li-profile" data-nav-fixed="profile">
            <a
              onClick={() =>
                this.setState({
                  userPopoverVisible: !this.state.userPopoverVisible,
                })
              }
              className="s_profile_bar_textlink"
              id="UserProfile"
            >
              <div className="s-list-default-btn-default-icon s-mn-icontext">
                <AccountCircleOutlined fontSize={'default'} />
              </div>
              {user}
            </a>
          </li>
        );
      }

      const hasNavContent = entries.length > 0 || !!admin || isAuthenticated;

      header.push(
        hasNavContent ? (
          // App bar: logo pinned left, nav centred, user menu pinned right
          // (see .s-header-appbar in global.css).
          <header id="s-header" className="s-header-appbar" key="header">
            <Link to="/site/dashboard" className="s-appbar-logo" aria-label="Fusion Inscribe home">
              <img src={FusionBannerTrans} alt="Fusion Inscribe" />
            </Link>
            <TopNav entries={entries} admin={admin} userSession={userSession} />
          </header>
        ) : (
          // No nav (login / setup): centred logo band on its own. #s-header's
          // border-bottom is the only separator needed here.
          <header id="s-header" key="header">
            <div className="s-header-band" style={{ backgroundColor: '#ffffff', textAlign: 'center' }}>
              <img
                src={FusionBannerTrans}
                alt="Fusion Inscribe"
                style={{ height: '52px', width: 'auto', margin: '8px 0' }}
              />
            </div>
          </header>
        )
      );
    } else header.push(<div key="header-empty" />);

    return (
      <div>
        {header}
        {userPopover}
      </div>
    );
  }
}

export default withRouter(connect((state) => state)(Header));
